<template>
  <div class="tracking-page">
    <tracking-page-header
      title="Fixes & follow-up"
      subtitle="Track whether a fix actually reduced the same problem. Useful for daily optimisation meetings."
      :from-date="fromDate"
      :to-date="toDate"
      :loading="loading"
      :chat-filters="chatFilters"
      :page-summary="pageSummary"
      @apply="fromDate = $event.fromDate; toDate = $event.toDate; loadData()"
      @refresh="loadData"
    />

    <div class="trk-panel mb-3">
      <div class="trk-panel-head"><div><h2>Problems still needing attention</h2><p>Click “Mark fix deployed” after your team ships a change</p></div></div>
      <div class="trk-panel-body p-0">
        <table class="table trk-table mb-0" v-trk-resizable="'issues-open'">
          <thead>
            <tr>
              <th>Problem</th>
              <th>Step</th>
              <th>Times</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedFingerprints" :key="row.fingerprint">
              <td>
                <strong>{{ prettyError(row.errorCode) }}</strong>
                <div class="small text-muted">{{ row.eventName }}</div>
              </td>
              <td>{{ prettyStep(row.step) }}</td>
              <td>{{ formatNumber(row.count) }}</td>
              <td>
                <span class="trk-pill" :class="row.regressed ? 'bad' : (row.status === 'RESOLVED' ? 'ok' : 'warn')">
                  {{ row.regressed ? 'Came back' : prettyStatusLabel(row.status) }}
                </span>
              </td>
              <td class="text-right">
                <button class="btn trk-btn-ghost btn-sm" type="button" @click="annotate(row)">Mark fix deployed</button>
              </td>
            </tr>
            <tr v-if="!(issues.fingerprints || []).length">
              <td colspan="5"><div class="trk-empty"><strong>No failure fingerprints</strong>Nothing to track right now.</div></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="trk-panel-body" v-if="totalFingerprints > fingerprintsSize">
        <b-pagination v-model="fingerprintsPage" :total-rows="totalFingerprints" :per-page="fingerprintsSize" />
      </div>
    </div>

    <div class="trk-panel">
      <div class="trk-panel-head"><div><h2>Change history</h2><p>What was fixed and when</p></div></div>
      <div class="trk-panel-body p-0">
        <table class="table trk-table mb-0" v-trk-resizable="'issues-history'">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Problem</th>
              <th>Status</th>
              <th>Deployed</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedAnnotations" :key="row.id">
              <td>{{ row.ticketReference || '-' }}</td>
              <td class="small">{{ row.errorFingerprint }}</td>
              <td>{{ prettyStatusLabel(row.status) }}</td>
              <td>{{ formatWhen(row.deployedAt) }}</td>
              <td>{{ row.notes || row.expectedResult || '-' }}</td>
            </tr>
            <tr v-if="!(issues.annotations || []).length">
              <td colspan="5" class="text-muted p-3">No change notes yet</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="trk-panel-body" v-if="totalAnnotations > annotationsSize">
        <b-pagination v-model="annotationsPage" :total-rows="totalAnnotations" :per-page="annotationsSize" />
      </div>
    </div>

    <b-modal
      id="mark-fix-deployed-modal"
      v-model="annotateOpen"
      title="Mark fix deployed"
      centered
      ok-title="Save"
      ok-variant="success"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      :ok-disabled="savingAnnotation"
      @ok="submitAnnotation"
      @hidden="resetAnnotateForm"
    >
      <p class="text-muted small mb-3" v-if="annotateRow">
        {{ prettyError(annotateRow.errorCode) }}
        <span v-if="annotateRow.step"> · {{ prettyStep(annotateRow.step) }}</span>
      </p>
      <div class="form-group">
        <label for="annotate-ticket">Ticket reference <span class="text-muted">(optional)</span></label>
        <input
          id="annotate-ticket"
          class="form-control"
          type="text"
          v-model.trim="annotateForm.ticket"
          placeholder="e.g. JIRA-123"
          @keyup.enter="focusNotes"
        />
      </div>
      <div class="form-group mb-0">
        <label for="annotate-notes">What did you fix / what should improve?</label>
        <textarea
          id="annotate-notes"
          ref="annotateNotes"
          class="form-control"
          rows="3"
          v-model="annotateForm.notes"
          placeholder="Fix deployed — monitor this problem for a few days"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';
import {
  defaultDateRange,
  formatNumber,
  prettyError,
  prettyStep,
  rangeParams,
  formatUkDateTime
} from './trackingShared';

export default {
  name: 'TrackingIssues',
  components: { TrackingPageHeader },
  data() {
    const range = defaultDateRange(7);
    return {
      fromDate: range.fromDate,
      toDate: range.toDate,
      issues: {},
      loading: false,
      annotateOpen: false,
      annotateRow: null,
      savingAnnotation: false,
      annotateForm: {
        ticket: '',
        notes: 'Fix deployed — monitor this problem for a few days'
      },
      fingerprintsPage: 1,
      fingerprintsSize: 20,
      annotationsPage: 1,
      annotationsSize: 20
    };
  },
  created() { this.loadData(); },
  computed: {
    chatFilters() {
      return rangeParams(this.fromDate, this.toDate);
    },
    pageSummary() {
      const fps = (this.issues.fingerprints || []).length;
      const regressed = (this.issues.fingerprints || []).filter((f) => f.regressed).length;
      return `${fps} tracked problems` + (regressed ? `; ${regressed} regressed` : '');
    },
    totalFingerprints() {
      return (this.issues.fingerprints || []).length;
    },
    pagedFingerprints() {
      const start = (this.fingerprintsPage - 1) * this.fingerprintsSize;
      return (this.issues.fingerprints || []).slice(start, start + this.fingerprintsSize);
    },
    totalAnnotations() {
      return (this.issues.annotations || []).length;
    },
    pagedAnnotations() {
      const start = (this.annotationsPage - 1) * this.annotationsSize;
      return (this.issues.annotations || []).slice(start, start + this.annotationsSize);
    }
  },
  methods: {
    formatNumber,
    prettyError,
    prettyStep,
    prettyStatusLabel(status) {
      const map = {
        OPEN: 'Open',
        UNDER_INVESTIGATION: 'Investigating',
        FIX_DEPLOYED: 'Fix deployed',
        MONITORING: 'Monitoring',
        RESOLVED: 'Resolved',
        REGRESSED: 'Came back'
      };
      return map[status] || status || '-';
    },
    formatWhen(v) {
      return formatUkDateTime(v);
    },
    loadData() {
      this.loading = true;
      this.fingerprintsPage = 1;
      this.annotationsPage = 1;
      this.$onboardingTrackingService.getIssues(rangeParams(this.fromDate, this.toDate))
        .then((res) => { this.issues = (res && res.data) || {}; })
        .finally(() => { this.loading = false; });
    },
    annotate(row) {
      this.annotateRow = row;
      this.annotateForm = {
        ticket: '',
        notes: 'Fix deployed — monitor this problem for a few days'
      };
      this.annotateOpen = true;
    },
    focusNotes() {
      const el = this.$refs.annotateNotes;
      if (el && el.focus) el.focus();
    },
    resetAnnotateForm() {
      this.annotateRow = null;
      this.savingAnnotation = false;
      this.annotateForm = {
        ticket: '',
        notes: 'Fix deployed — monitor this problem for a few days'
      };
    },
    submitAnnotation(bvEvent) {
      if (!this.annotateRow) {
        bvEvent.preventDefault();
        return;
      }
      const notes = (this.annotateForm.notes || '').trim();
      if (!notes) {
        bvEvent.preventDefault();
        return;
      }
      bvEvent.preventDefault();
      this.savingAnnotation = true;
      this.$onboardingTrackingService.createChangeAnnotation({
        ticketReference: this.annotateForm.ticket || undefined,
        affectedStep: this.annotateRow.step,
        errorFingerprint: this.annotateRow.fingerprint,
        status: 'FIX_DEPLOYED',
        deployedAt: new Date().toISOString(),
        expectedResult: notes,
        notes,
        createdBy: 'admin'
      }).then(() => {
        this.annotateOpen = false;
        this.loadData();
      }).finally(() => {
        this.savingAnnotation = false;
      });
    }
  }
};
</script>
